import { useCallback, useMemo, useState } from 'react';
import { formatRelativeTime } from '@repo/shared';
import { useNavigate } from '@tanstack/react-router';

import { useCustomers } from '@/api/generated';
import { CustomersHeader } from '@/components/blocks/customers/customers-header';
import { CustomersSearch } from '@/components/blocks/customers/customers-search';
import { CustomersTable } from '@/components/blocks/customers/customers-table';
import { CustomersTableSkeleton } from '@/components/blocks/customers/customers-table-skeleton';
import PageLayout from '@/components/blocks/layout/page-layout';
import { CommonError } from '@/components/ui/common-error';

const Customers = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, isError, refetch, isFetching, dataUpdatedAt } =
    useCustomers();

  const formatedUpdatedAt = useMemo(
    () => formatRelativeTime(dataUpdatedAt),
    [dataUpdatedAt]
  );

  const customers = useMemo(() => data?.items ?? [], [data]);

  const filteredCustomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return customers;
    }

    return customers.filter(
      (customer) =>
        customer.displayName.toLowerCase().includes(query) ||
        String(customer.id).includes(query)
    );
  }, [customers, searchQuery]);

  const handleSelectCustomer = async (customerId: number) =>
    navigate({
      to: '/app/shelves/$customerId',
      params: { customerId: String(customerId) },
    });

  const refetchCustomers = useCallback(async () => {
    await refetch();
  }, [refetch]);

  if (isLoading) {
    return <CustomersTableSkeleton />;
  }

  if (isError || !data) {
    return <CommonError />;
  }

  return (
    <PageLayout>
      <CustomersHeader
        isFetching={isFetching}
        formatedUpdatedAt={formatedUpdatedAt}
        refetchCustomers={refetchCustomers}
      />
      <CustomersSearch
        filteredCustomers={filteredCustomers}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
      <CustomersTable
        filteredCustomers={filteredCustomers}
        customers={customers}
        handleSelectCustomer={handleSelectCustomer}
      />
    </PageLayout>
  );
};

export default Customers;
